import { useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export const useScrollAnimation = () => {
  useEffect(() => {
    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      // Navbar animation on load - target only the mobile bottom navbar
      const navbar = document.querySelector('nav[data-animate="navbar"]')
      if (navbar) {
        gsap.from(navbar, {
          y: -100,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
        })

        // Navbar items stagger
        const navItems = navbar.querySelectorAll('a, button')
        gsap.from(navItems, {
          y: -20,
          opacity: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'back.out(1.7)',
          delay: 0.5,
        })
      }

      // Animate sections on scroll - each section triggers independently
      const sections = document.querySelectorAll('section')
      
      sections.forEach((section, sectionIndex) => {
        // Create a timeline for each section
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            end: 'top 25%',
            toggleActions: 'play none none reverse',
          }
        })

        // Animate headings within this section FIRST
        const headings = section.querySelectorAll('h1, h2, h3')
        if (headings.length > 0) {
          tl.fromTo(
            headings,
            { opacity: 0, y: -30, scale: 0.9 },
            { opacity: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.15, ease: 'back.out(1.7)' },
            0
          )
        }

        // Then paragraphs
        const paragraphs = section.querySelectorAll('p')
        if (paragraphs.length > 0) {
          tl.fromTo(
            paragraphs,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out' },
            '-=0.4'
          )
        }

        // Then buttons
        const buttons = section.querySelectorAll('button, a[class*="button"]')
        if (buttons.length > 0) {
          tl.fromTo(
            buttons,
            { opacity: 0, scale: 0.8 },
            { opacity: 1, scale: 1, duration: 0.5, stagger: 0.1, ease: 'elastic.out(1, 0.5)' },
            '-=0.3'
          )
        }

        // Icons with rotation
        const icons = section.querySelectorAll('svg')
        if (icons.length > 0) {
          tl.fromTo(
            icons,
            { opacity: 0, scale: 0, rotation: -180 },
            { opacity: 1, scale: 1, rotation: 0, duration: 0.5, stagger: 0.02, ease: 'back.out(1.7)' },
            '-=0.4'
          )
        }

        // Cards
        const cards = section.querySelectorAll('[class*="card"], [class*="bg-gradient"]')
        if (cards.length > 0) {
          tl.fromTo(
            cards,
            { opacity: 0, y: 50, scale: 0.95 },
            { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.12, ease: 'power3.out' },
            '-=0.3'
          )
        }

        // Images
        const images = section.querySelectorAll('img')
        if (images.length > 0) {
          tl.fromTo(
            images,
            { opacity: 0, scale: 1.15, filter: 'blur(10px)' },
            { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 1, ease: 'power3.out' },
            '-=0.5'
          )
        }
      })

      // Parallax scrolling effect
      gsap.utils.toArray('.parallax').forEach((element) => {
        gsap.to(element, {
          y: () => element.offsetHeight * 0.5,
          ease: 'none',
          scrollTrigger: {
            trigger: element,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        })
      })

      // Footer animation
      const footer = document.querySelector('footer')
      if (footer) {
        gsap.fromTo(
          footer,
          {
            opacity: 0,
            y: 50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: footer,
              start: 'top 90%',
              toggleActions: 'play none none reverse',
            },
          }
        )
      }
    }, 100)

    return () => {
      clearTimeout(timer)
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
  }, [])
}
